import useSWR from "swr";
import { type PlotRequestDetailResponse } from "@/features/map/types";
import { useApiClient } from "@/hooks/useApiClient";
import { type AxiosError } from "axios";
import { getPlotRequestDetailAPI } from "@/features/map/api/getPlotRequestDetailAPI";

export const usePlotRequestDetail = (
  id: number | undefined,
  options?: {
    revalidateOnMount?: boolean;
  }
): {
  data: PlotRequestDetailResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  mutate: (
    data?:
      | PlotRequestDetailResponse
      | Promise<PlotRequestDetailResponse>
      | undefined,
    shouldRevalidate?: boolean
  ) => Promise<PlotRequestDetailResponse | undefined>;
} => {
  const { apiClient } = useApiClient();

  const fetcher = async (): Promise<PlotRequestDetailResponse | undefined> => {
    if (id === undefined) {
      return undefined;
    }
    return await getPlotRequestDetailAPI(apiClient, id);
  };

  const swrResponse = useSWR<PlotRequestDetailResponse | undefined, AxiosError>(
    id !== undefined ? `/map/plot-request/${id}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      revalidateOnMount: options?.revalidateOnMount ?? true,
    }
  );

  return {
    data: swrResponse.data,
    isLoading: !swrResponse.error && !swrResponse.data && id !== undefined,
    isError: !!swrResponse.error,
    mutate: swrResponse.mutate,
  };
};
